import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  TextField,
  MenuItem,
  Typography,
  Avatar,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  PersonAdd,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  Phone,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LeadListTable from '../components/leads/LeadListTable';
import { useLeads, useExportLeads, useDeleteLead } from '../hooks/useLeads';
import { LeadStatus, LeadSource, LeadFilters } from '../types/lead';

const LeadsPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('');
  const [sourceFilter, setSourceFilter] = useState<LeadSource | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Sorting
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Debounce search term
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [statusFilter, sourceFilter, dateFrom, dateTo]);

  // Fetch leads with filters
  const { data, isLoading, error, refetch } = useLeads({
    page,
    per_page: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter ? [statusFilter] : undefined,
    source: sourceFilter ? [sourceFilter] : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  const leads = data?.items || [];
  const total = data?.total || 0;

  // Export leads mutation
  const exportLeads = useExportLeads();
  const deleteLead = useDeleteLead();

  const handleCreateLead = () => {
    navigate('/admin/leads/create');
  };

  const handleExportLeads = async () => {
    // Build filter object with current filters
    const filters: LeadFilters = {};
    
    if (debouncedSearch) filters.search = debouncedSearch;
    if (statusFilter) filters.status = [statusFilter];
    if (sourceFilter) filters.source = [sourceFilter];
    if (dateFrom) filters.date_from = dateFrom;
    if (dateTo) filters.date_to = dateTo;
    
    // Trigger export with current filters
    await exportLeads.mutateAsync(filters);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setSourceFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const handleRefresh = () => {
    // Force refetch of leads data
    refetch();
  };

  // Calculate stats
  const stats = React.useMemo(() => {
    return {
      total: total || 0,
      new: leads.filter(l => l.status === LeadStatus.NEW).length,
      contacted: leads.filter(l => l.status === LeadStatus.CONTACTED).length,
      qualified: leads.filter(l => l.status === LeadStatus.QUALIFIED).length,
    };
  }, [leads, total]);

  return (
    <Box sx={{ p: 3, bgcolor: '#FAFAFA', minHeight: '100vh' }}>
      {/* Header with Brand Gradient */}
      <Paper
        sx={{
          background: 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
          color: 'white',
          p: 3,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Lead Management
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Track and manage your sales leads efficiently
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => navigate('/admin/leads/import')}
              sx={{
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Import Leads
            </Button>
            <Tooltip 
              title={
                debouncedSearch || statusFilter || sourceFilter || dateFrom || dateTo
                  ? 'Export filtered leads to CSV'
                  : 'Export all leads to CSV'
              }
            >
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportLeads}
                disabled={exportLeads.isPending}
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  },
                  '&:disabled': {
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'rgba(255,255,255,0.5)'
                  }
                }}
              >
                {exportLeads.isPending ? 'Exporting...' : 'Export Leads'}
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateLead}
              sx={{
                bgcolor: '#f44336',
                '&:hover': { bgcolor: '#c62828' },
                boxShadow: '0 2px 8px rgba(244,67,54,0.3)'
              }}
            >
              Add Lead
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>
                  <PersonAdd />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="white" fontWeight={600}>
                    {stats.total}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Total Leads
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(76,175,80,0.2)', backdropFilter: 'blur(10px)' }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(76,175,80,0.3)', color: 'white' }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="white" fontWeight={600}>
                    {stats.qualified}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Qualified Leads
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(255,152,0,0.2)', backdropFilter: 'blur(10px)' }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,152,0,0.3)', color: 'white' }}>
                  <HourglassEmpty />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="white" fontWeight={600}>
                    {stats.new}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    New Leads
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(33,150,243,0.2)', backdropFilter: 'blur(10px)' }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(33,150,243,0.3)', color: 'white' }}>
                  <Phone />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="white" fontWeight={600}>
                    {stats.contacted}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Contacted
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Search and Filters */}
      <Card sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name, email, phone, company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              fullWidth
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as LeadStatus | '')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: statusFilter && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setStatusFilter('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value={LeadStatus.NEW}>New</MenuItem>
              <MenuItem value={LeadStatus.CONTACTED}>Contacted</MenuItem>
              <MenuItem value={LeadStatus.QUALIFIED}>Qualified</MenuItem>
              <MenuItem value={LeadStatus.PROPOSAL_SENT}>Proposal Sent</MenuItem>
              <MenuItem value={LeadStatus.NEGOTIATION}>Negotiation</MenuItem>
              <MenuItem value={LeadStatus.CONVERTED}>Converted</MenuItem>
              <MenuItem value={LeadStatus.LOST}>Lost</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              fullWidth
              label="Source"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as LeadSource | '')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: sourceFilter && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSourceFilter('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">All Sources</MenuItem>
              <MenuItem value={LeadSource.WEBSITE}>Website</MenuItem>
              <MenuItem value={LeadSource.REFERRAL}>Referral</MenuItem>
              <MenuItem value={LeadSource.SOCIAL_MEDIA}>Social Media</MenuItem>
              <MenuItem value={LeadSource.EMAIL_CAMPAIGN}>Email Campaign</MenuItem>
              <MenuItem value={LeadSource.TRADE_SHOW}>Trade Show</MenuItem>
              <MenuItem value={LeadSource.COLD_CALL}>Cold Call</MenuItem>
              <MenuItem value={LeadSource.OTHER}>Other</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>

        {/* Filter Actions */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {(searchTerm || statusFilter || sourceFilter || dateFrom || dateTo) && (
              <>
                <Typography variant="caption" color="text.secondary">
                  Active Filters:
                </Typography>
                {searchTerm && (
                  <Chip
                    label={`Search: "${searchTerm}"`}
                    size="small"
                    onDelete={() => setSearchTerm('')}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {statusFilter && (
                  <Chip
                    label={`Status: ${statusFilter}`}
                    size="small"
                    onDelete={() => setStatusFilter('')}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {sourceFilter && (
                  <Chip
                    label={`Source: ${sourceFilter}`}
                    size="small"
                    onDelete={() => setSourceFilter('')}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {(dateFrom || dateTo) && (
                  <Chip
                    label={`Date: ${dateFrom || '...'} to ${dateTo || '...'}`}
                    size="small"
                    onDelete={() => {
                      setDateFrom('');
                      setDateTo('');
                    }}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Clear all filters">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearFilters}
                  disabled={!searchTerm && !statusFilter && !sourceFilter && !dateFrom && !dateTo}
                  startIcon={<FilterListIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Clear
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Refresh leads">
              <IconButton 
                onClick={handleRefresh} 
                disabled={isLoading}
                sx={{ 
                  bgcolor: 'primary.50',
                  '&:hover': { bgcolor: 'primary.100' }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Card>

      {/* Error State */}
      {error && (
        <Card sx={{ p: 3, mb: 3, bgcolor: 'error.50', borderRadius: 3 }}>
          <Typography color="error">
            Failed to load leads. Please try again.
          </Typography>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Leads Table */}
      {!isLoading && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <LeadListTable
            leads={leads}
            loading={isLoading}
            page={page - 1}
            pageSize={pageSize}
            totalCount={total}
            onPageChange={(newPage) => setPage(newPage + 1)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            onSortChange={(newSortBy, newSortOrder) => {
              setSortBy(newSortBy);
              setSortOrder(newSortOrder);
              setPage(1);
            }}
            onView={(leadId) => navigate(`/admin/leads/${leadId}`)}
            onEdit={(leadId) => navigate(`/admin/leads/${leadId}/edit`)}
            onDelete={(leadId) => {
              if (window.confirm('Are you sure you want to delete this lead?')) {
                deleteLead.mutate(leadId);
              }
            }}
          />
        </Card>
      )}
    </Box>
  );
};

export default LeadsPage;
